import React, { useEffect, useRef, useState } from "react";
import copyIcon from "../../assets/copy.svg";
import { ReactComponent as CopyColoredIcon } from "../../assets/CopyIcon.svg";
import exportIcon from "../../assets/export.svg";
import backlogIcon from "../../assets/backlog.svg";
import archiveIcon from "../../assets/archive.svg";
import { ReactComponent as UserLogo } from "../../assets/user-icon-color.svg";
import { ReactComponent as EditLogo } from "../../assets/edit-icon-color.svg";
import { ReactComponent as ImageLogo } from "../../assets/image-icon-color.svg";
import { ReactComponent as DeleteLogo } from "../../assets/delete-icon-color.svg";
import { ReactComponent as ArchiveLogo } from "../../assets/archive-color.svg";
import { ReactComponent as CloseIcon } from "../../assets/cross-color.svg";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import "./ProjectSharePopup.css";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import useIsMobile from "../../hooks/useIsMobile";
import { useNavigate } from "react-router";
import {
  fetchSharedLinks,
  generateShareLink,
  revokeSharedLink,
} from "../../store/usersSlice";
import { updateProjectNote } from "../../store/projectsSlice";

const ACCESS_OPTIONS = [
  { value: "VIEWER", label: "для просмотра" },
  { value: "COMMENTER", label: "для комментирования" },
  { value: "ADMIN", label: "для редактирования" },
];

const ProjectSharePopup = ({
  project,
  versionId = null,
  onClose = () => {},
  onOpenTeamScreen = () => {},
  onOpenBacklog,
  onRename = () => {},
  onChangeCover = () => {},
  onDelete = () => {},
  onArchive = () => {},
  className = "",
  isModal = false,
}) => {
  const popupRef = useRef();
  const [access, setAccess] = useState("VIEWER");
  const [comment, setComment] = useState(project.releaseNotes || "");
  const [emailValue, setEmailValue] = useState("");
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [linkLoading, setLinkLoading] = useState(true);
  const [sharedLinksArray, setSharedLinksArray] = useState([]);
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  let updateCommentTimeout = useRef();

  let copiedLinkTimeout = null;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        event.target.id !== "project-share-popup-block" &&
        event.target.className !== "projectScreen-title" &&
        event.target.id !== "arrow-down"
      ) {
        event.preventDefault();
        onClose();
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    setLinkLoading(true);
    let values = {
      resourceId: versionId ? versionId : project.id,
      resourceType: versionId ? "VIDEO" : "PROJECT",
      role: access,
    };
    if (access === "ADMIN") {
      values = {
        ...values,
        email: emailValue,
      };
      setEmailValue("");
    }
    if (access !== "ADMIN")
      dispatch(generateShareLink(values)).then((res) => {
        setLinkLoading(false);
        if (!res.error) {
          setShareLink(res.payload);
        }
      });
  }, [access]);

  useEffect(() => {
    dispatch(
      fetchSharedLinks({
        resourceId: project.id,
        resourceType: "PROJECT",
      })
    ).then((res) => {
      if (!res.error) {
        const filteredLinks = res.payload.filter(
          (link) => link.role === "ADMIN" && !!link.email
        );
        setSharedLinksArray(filteredLinks);
      }
    });
  }, [project]);

  const handleCopy = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyExistingLink = (link, id) => {
    navigator.clipboard.writeText(shareLink);
    clearTimeout(copiedLinkTimeout);
    setCopiedLinkId(id);
    copiedLinkTimeout = setTimeout(() => setCopiedLinkId(null), 2000);
  };

  const handleChangeComment = (value) => {
    setIsSaved(false);
    setComment(value);
    clearTimeout(updateCommentTimeout.current);
    updateCommentTimeout.current = setTimeout(() => {
      dispatch(
        updateProjectNote({ projectId: project.id, releaseNote: value })
      ).then((res) => {
        if (!res.error) {
          setIsSaved(true);
        }
      });
    }, 1000);
  };

  const handleChangeEmailValue = (value) => {
    if (shareLink) {
      setShareLink("");
    }
    setEmailValue(value.trim());
    // clearTimeout(updateCommentTimeout.current);
    // if (validateEmail(value)) {
    //   updateCommentTimeout.current = setTimeout(() => {
    //     dispatch(
    //       generateShareLink({
    //         resourceId: video.id,
    //         resourceType: "VIDEO",
    //         role: access,
    //         email: value,
    //       })
    //     ).then((res) => {
    //       if (!res.error) {
    //         setShareLink(res.payload);
    //       }
    //     });
    // }, 1000);
    // }
  };

  const handleInviteSharedUser = () => {
    let data = {
      resourceId: project.id,
      resourceType: "PROJECT",
      role: access,
    };
    if (emailValue) {
      data = {
        ...data,
        email: emailValue,
      };
    }
    dispatch(generateShareLink(data)).then((res) => {
      if (!res.error) {
        setShareLink(res.payload);
        navigator.clipboard.writeText(res.payload);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        dispatch(
          fetchSharedLinks({
            resourceId: project.id,
            resourceType: "PROJECT",
          })
        ).then((res) => {
          if (!res.error) {
            const filteredLinks = res.payload.filter(
              (link) => link.role === "ADMIN" && !!link.email
            );
            setSharedLinksArray(filteredLinks);
          }
        });
      }
    });
  };

  const onPressRevokeLink = (linkId) => {
    if (confirm("Вы действительно хотите удалить эту ссылку?")) {
      dispatch(revokeSharedLink({ linkId })).then((res) => {
        if (!res.error) {
          dispatch(
            fetchSharedLinks({
              resourceId: project.id,
              resourceType: "PROJECT",
            })
          ).then((res) => {
            if (!res.error) {
              const filteredLinks = res.payload.filter(
                (link) => link.role === "ADMIN" && !!link.email
              );
              setSharedLinksArray(filteredLinks);
            }
          });
        }
      });
    }
  };

  return (
    <div
      ref={popupRef}
      className={`project-share-popup-context-menu ${className}`}
    >
      <div className="project-share-popup-row project-share-popup-link-row">
        <span className="project-share-popup-label">Поделиться ссылкой</span>
        <div className="project-share-popup-access-select">
          {false ? (
            <div
              style={{
                width: 200,
                paddingTop: 6,
                borderRadius: 4,
                paddingBottom: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "white",
                color: "#333B4B",
                fontSize: 14,
                fontWeight: 500,
                fontFamily: "NT Somic",
              }}
            >
              Скопирована
            </div>
          ) : (
            <select value={access} onChange={(e) => setAccess(e.target.value)}>
              {ACCESS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
      {access !== "ADMIN" && (
        <div className="project-share-popup-row">
          <input
            className="project-share-popup-link-input"
            style={{ background: "#14263e" }}
            value={linkLoading ? "..." : shareLink}
            readOnly
            placeholder="Загрузка..."
          />
          <button className="project-share-popup-copy-btn" onClick={handleCopy}>
            <img src={copyIcon} alt="copy" style={{ width: 18, height: 18 }} />
          </button>
        </div>
      )}
      {copied && access !== "ADMIN" && (
        <span className="project-share-popup-copied">
          {"Ссылка скопирована в буфер!"}
        </span>
      )}
      {access === "ADMIN" && (
        <span
          style={{
            display: "block",
            textAlign: "left",
            color: "#6e7986",
            fontSize: 14,
            lineHeight: 1,
            marginBottom: 16,
          }}
        >
          Введите почту пользователя, чтобы расшарить проект с возможностью
          редактирования
        </span>
      )}
      {access === "ADMIN" && (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            marginBottom: 12,
          }}
        >
          <input
            className="share-popup-email"
            placeholder="Электронная почта"
            style={{
              marginBottom: 0,
              minHeight: 18,
              height: 32,
              padding: "0px 8px",
              background: "#152c4e",
              borderColor: "#4056d0",
            }}
            value={emailValue}
            onChange={(e) => {
              handleChangeEmailValue(e.target.value);
            }}
          />
          <PrimaryButton
            onClick={() => {
              handleInviteSharedUser();
            }}
            isLoading={false}
            text={"Пригласить"}
            style={{
              padding: "8px 32px",
              fontSize: 14,
              fontWeight: "bold",
              borderRadius: 8,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          />
        </div>
      )}
      {copied && access === "ADMIN" && (
        <span className="project-share-popup-copied">
          {"Ссылка скопирована в буфер!"}
        </span>
      )}
      {!!sharedLinksArray.length && access === "ADMIN" && (
        <div
          style={{
            width: "100%",
            marginBottom: 16,
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
            justifyContent: "flex-start",
            flexDirection: "column",
          }}
        >
          <span className="project-share-popup-label">
            Приглашенные пользователи:
          </span>
          {sharedLinksArray.map((link) => (
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 13, color: "#6e7986", fontWeight: 500 }}>
                {link.email}
              </span>
              <input
                className="project-share-popup-link-input"
                style={{
                  background: "#14263e",
                  flex: 1,
                  marginLeft: 12,
                  marginRight: 8,
                  height: 28,
                  fontSize: 12,
                  fontWeight: 500,
                  border: "none",
                  color: copiedLinkId === link.id ? "#79c979" : "#3496ff",
                  padding: "0px 8px",
                  borderRadius: 10,
                }}
                value={
                  copiedLinkId === link.id
                    ? "Ссылка скопирована в буфер"
                    : link.url
                }
                readOnly
                placeholder="Загрузка..."
              />
              <div
                style={{
                  height: 18,
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  cursor: "pointer",
                }}
                onClick={() => handleCopyExistingLink(link.url, link.id)}
              >
                <CopyColoredIcon
                  style={{ height: 18, width: 18, color: "#3496ff" }}
                />
              </div>
              <div
                style={{
                  height: 18,
                  marginLeft: 5,
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={() => onPressRevokeLink(link.id)}
              >
                <CloseIcon
                  style={{
                    height: 24,
                    width: 24,
                    color: "#d65757",
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
      {(!isModal && (
        <>
          <textarea
            className="project-share-popup-comment"
            placeholder={t("Комментарий к проекту (доступен всем по ссылке)")}
            value={comment}
            style={{ background: "#101928" }}
            onChange={(e) => handleChangeComment(e.target.value)}
          />
          <div className="project-share-popup-actions">
            <button
              className="project-share-popup-action"
              onClick={() => onOpenTeamScreen()}
            >
              <UserLogo style={{ marginRight: 5 }} />
              {"Команда"}
            </button>
            {/* <button
          className="project-share-popup-action"
          onClick={() => {
            if (onClose) onClose();
            if (onOpenBacklog) onOpenBacklog();
          }}
        >
          <img src={backlogIcon} alt="backlog" /> {t("share.folderBacklog")}
        </button> */}
            <button
              className="project-share-popup-action"
              onClick={(e) => onRename(e)}
            >
              <EditLogo style={{ marginRight: 5 }} />
              {t("share.renameFolder")}
            </button>
            <button
              className="project-share-popup-action"
              onClick={(e) => onChangeCover(e)}
            >
              <ImageLogo style={{ marginRight: 5 }} /> {t("share.changeCover")}
            </button>
            {/* <button
              className="project-share-popup-action"
              onClick={(e) => onArchive(project.id, e)}
            >
              <ArchiveLogo style={{ marginRight: 5 }} /> {t("share.archive")}
            </button> */}
            <button
              className="project-share-popup-action"
              onClick={(e) => onDelete(project.id, e)}
            >
              <DeleteLogo style={{ marginRight: 5 }} /> {t("share.delete")}
            </button>
          </div>
        </>
      )) ||
        null}
    </div>
  );
};

export default ProjectSharePopup;
